import { useState } from "react";
import { Box, Flex, Text, useToast } from "@chakra-ui/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { NormalisedAccountBranding } from "models/types/agency-settings";
import { useForm } from "react-hook-form";
import { useRevalidator } from "react-router-dom";
import AccentColorHelpImage from "assets/help-images/accent-color.png";
import NavigationColorHelpImage from "assets/help-images/navigation-color.png";
import PrimaryLogoHelpImage from "assets/help-images/primary-logo.png";
import SecondaryLogoHelpImage from "assets/help-images/secondary-logo.png";
import { UnexpectedErrorContent } from "components/errors/components/unexpected-error-content";
import { Form } from "components/form";
import { FormField } from "components/form-field";
import { HelpPopover } from "components/help-popover";
import { ColorPickerInput } from "components/inputs/color-picker-input";
import { LoadingScreen } from "components/loading-screen";
import { ScreenWrapper } from "components/screen-wrapper";
import { AgencySettingsPageCards } from "modules/agency-settings/components/agency-settings-page/agency-settings-page-cards";
import { AgencySettingsPageTitle } from "modules/agency-settings/components/agency-settings-page/agency-settings-page-title";
import {
  accountBrandingModel,
  type AppearanceFormValues,
} from "modules/agency-settings/models/account-branding-model";
import { BrandingLogo } from "modules/agency-settings/screens/branding/components/branding-logo";
import { FontTabs } from "modules/agency-settings/screens/branding/components/font-tabs/font-tabs";
import { UploadFontModal } from "modules/agency-settings/screens/branding/components/upload-font-modal";
import { getTraceId } from "utils/get-trace-id";

export type BrandingScreenWithDataProps = {
  brandingData: NormalisedAccountBranding;
};

function BrandingScreenWithData({ brandingData }: BrandingScreenWithDataProps) {
  const toast = useToast();
  const [isUploadFontModalOpen, setIsUploadFontModalOpen] = useState(false);
  const queryClient = useQueryClient();
  const revalidator = useRevalidator();

  const form = useForm<AppearanceFormValues>({
    defaultValues: { ...brandingData },
  });

  // Update branding mutation
  const updateBrandingMutation = useMutation({
    ...accountBrandingModel.mutations.update(),
    onSuccess: () => {
      toast({
        title: "Branding updated",
        description: "Your branding settings have been updated successfully.",
        status: "success",
        duration: 5000,
        isClosable: true,
      });

      queryClient.invalidateQueries({
        queryKey: accountBrandingModel.queries.get().queryKey,
      });
      revalidator.revalidate();
    },
    onError: () => {
      toast({
        title: "Update failed",
        description: (
          <>
            <Text>
              There was an error updating your branding settings. Please try
              again.
            </Text>
            <small>Trace ID: {getTraceId()}</small>
          </>
        ),
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    },
  });

  // Handle form submission
  const handleSubmit = async (values: AppearanceFormValues) => {
    await updateBrandingMutation.mutateAsync({
      ...values,
    });
    form.reset(values);
  };

  return (
    <ScreenWrapper>
      <Form
        id={"branding"}
        onSubmit={handleSubmit}
        form={form}
        enableSaveCancelOverlay
        ctaText={"Save"}
      >
        <Flex direction={"column"} gap={8}>
          <AgencySettingsPageTitle
            title={"Branding"}
            description={"Choose the look and feel of your Rex Home platforms"}
          />
          <AgencySettingsPageCards
            heading={"Logos"}
            description={"Add a primary and secondary logo"}
          >
            <Flex direction={"column"} gap={12}>
              <BrandingLogo
                fieldName={"primary_logo"}
                heading={"Primary logo"}
                description={
                  "Your primary logo will be used for most of your company branding on Rex Home platforms and notification emails."
                }
                recommendationText={
                  "We recommend a transparent PNG with an approximate ratio of 5:2 and a minimum height of 120px."
                }
                helpPopover={
                  <HelpPopover
                    title={"Primary logo"}
                    image={PrimaryLogoHelpImage}
                    description={
                      "The primary logo will appear in the top navigation, hero image, vendor presentations and notification emails. For the best outcome, we recommend using a transparent PNG logo that is 5:2 ratio with a minimum height of 120px."
                    }
                  />
                }
              />
              <BrandingLogo
                size={"small"}
                fieldName={"secondary_logo"}
                heading={"Secondary logo"}
                description={
                  " Your secondary logo will be used as a thumbnail image for contact details. Agents without profile images will have this logo displayed instead."
                }
                recommendationText={
                  " We recommend using a PNG logo that is 1:1 ratio and at least 240px x 240px."
                }
                helpPopover={
                  <HelpPopover
                    title={"Secondary logo"}
                    image={SecondaryLogoHelpImage}
                    description={
                      "The secondary logo will appear as a thumbnail for agency contact details. For the best outcome, we recommend using a PNG logo that is 1:1 ratio and at least 240px x 240px."
                    }
                  />
                }
              />
            </Flex>
          </AgencySettingsPageCards>

          <AgencySettingsPageCards
            heading={"Brand colors"}
            description={"Choose your own brand colors"}
          >
            <Flex direction={"column"} gap={12}>
              <Box>
                <Flex>
                  <FormField
                    name={"accent_color"}
                    label={
                      <>
                        Accent color
                        <HelpPopover
                          title={"Accent color"}
                          image={AccentColorHelpImage}
                          description={
                            "The accent colour will be used for banner backgrounds when a property image is not available and statistic icons."
                          }
                        />
                      </>
                    }
                    required
                    Input={ColorPickerInput}
                  />
                  <FormField
                    name={"navigation_color"}
                    required
                    label={
                      <>
                        Navigation color
                        <HelpPopover
                          title={"Navigation color"}
                          image={NavigationColorHelpImage}
                          description={
                            "The navigation colour will be used for your sidebar navigation."
                          }
                        />
                      </>
                    }
                    Input={ColorPickerInput}
                  />
                </Flex>
              </Box>
            </Flex>
          </AgencySettingsPageCards>

          <AgencySettingsPageCards
            heading={"Brand fonts"}
            description={"Choose your own brand fonts"}
          >
            <Flex direction={"column"} gap={12}>
              <Box>
                <FontTabs setIsUploadFontModalOpen={setIsUploadFontModalOpen} />
              </Box>
            </Flex>
          </AgencySettingsPageCards>
        </Flex>
      </Form>
      <UploadFontModal
        isOpen={isUploadFontModalOpen}
        onClose={() => setIsUploadFontModalOpen(false)}
      />
    </ScreenWrapper>
  );
}

export function BrandingScreen() {
  // Fetch branding data
  const {
    data: brandingData,
    isLoading,
    error,
  } = useQuery(accountBrandingModel.queries.get());

  if (error) {
    return <UnexpectedErrorContent error={error} />;
  }

  if (!brandingData || isLoading) {
    return <LoadingScreen loadingText={"Loading branding settings..."} />;
  }

  return <BrandingScreenWithData brandingData={brandingData} />;
}
